<template>
  <Dialog
    v-model:visible="dialogVisible"
    :header="isEditing ? 'Edit Chart' : 'New Chart'"
    modal
    class="w-11/12 md:w-1/2"
  >
    <div class="p-4 space-y-4">
      <!-- Chart Type -->
      <div class="field">
        <label class="block text-sm font-medium text-gray-700 mb-1">Chart Type</label>
        <Dropdown
          v-model="chartData.type"
          :options="chartTypes"
          optionLabel="name"
          optionValue="value"
          class="w-full"
        />
      </div>

      <!-- Visibility: own / team / both result screens -->
      <div class="field">
        <label class="block text-sm font-medium text-gray-700 mb-1">
          {{ $t('ChartDialog.visibilityLabel') }}
        </label>
        <Dropdown
          v-model="chartData.visibility"
          :options="visibilityOptions"
          optionLabel="name"
          optionValue="value"
          class="w-full"
        />
        <p
          v-if="barBoxplotVisibilityHint"
          class="text-xs text-amber-800 mt-2"
        >
          {{ barBoxplotVisibilityHint }}
        </p>
      </div>

      <!-- Bar chart: optional boxplot conversion when enough aggregated results (tenant minOfDatasetsForAggResults) -->
      <div
        v-if="chartData.type === 'bar'"
        class="field p-4 border rounded bg-amber-50/90"
      >
        <p class="text-sm text-gray-600 mb-3">
          {{ $t('ChartDialog.barBoxplotHint', { n: minDatasetsForAggDisplay }) }}
        </p>
        <div class="flex items-center mb-3">
          <input
            type="checkbox"
            id="barBoxplotEnable"
            v-model="chartData.barBoxplotWhenAggregated.enabled"
            class="mr-2"
            @change="onBarBoxplotEnabledChange"
          />
          <label for="barBoxplotEnable" class="text-sm font-medium text-gray-700">
            {{ $t('ChartDialog.barBoxplotEnable') }}
          </label>
        </div>
        <div
          v-if="chartData.barBoxplotWhenAggregated && chartData.barBoxplotWhenAggregated.enabled"
          class="space-y-2 ml-6"
        >
          <div class="flex items-center">
            <input
              id="barBoxplotOwn"
              v-model="chartData.barBoxplotWhenAggregated.ownResults"
              type="checkbox"
              class="mr-2"
            />
            <label for="barBoxplotOwn" class="text-sm text-gray-700">
              {{ $t('ChartDialog.barBoxplotOwnResults') }}
            </label>
          </div>
          <div class="flex items-center">
            <input
              id="barBoxplotTeam"
              v-model="chartData.barBoxplotWhenAggregated.teamResults"
              type="checkbox"
              class="mr-2"
            />
            <label for="barBoxplotTeam" class="text-sm text-gray-700">
              {{ $t('ChartDialog.barBoxplotTeamResults') }}
            </label>
          </div>
        </div>
      </div>

      <!-- Title Translations -->
      <div class="field">
        <label class="block text-sm font-medium text-gray-700 mb-1">Title (English)</label>
        <InputText v-model="chartData.title.en" class="w-full" />
      </div>
      <div class="field">
        <label class="block text-sm font-medium text-gray-700 mb-1">Title (German)</label>
        <InputText v-model="chartData.title.de" class="w-full" />
      </div>

      <!-- Description Translations -->
      <div class="field">
        <label class="block text-sm font-medium text-gray-700 mb-1">Description (English)</label>
        <InputText v-model="chartData.description.en" class="w-full" />
      </div>
      <div class="field">
        <label class="block text-sm font-medium text-gray-700 mb-1">Description (German)</label>
        <InputText v-model="chartData.description.de" class="w-full" />
      </div>

      <!-- Dimensions -->
      <div class="field">
        <label class="block text-sm font-medium text-gray-700 mb-1">Dimensions</label>
        <MultiSelect
          v-model="chartData.dimensions"
          :options="availableDimensions"
          :optionLabel="appLanguage === 'en' ? 'translation.en' : 'translation.de'"
          optionValue="value"
          class="w-full"
          :filter="true"
        />
      </div>

      <!-- Dimension Averaging Configuration -->
      <div v-if="chartData.dimensions && chartData.dimensions.length > 1"
         class="field p-4 border rounded bg-blue-50">
        <h4 class="text-lg font-medium text-gray-700 mb-3">Dimension Averaging</h4>

        <!-- Enable Averaging Toggle -->
        <div class="flex items-center mb-4">
          <input
            type="checkbox"
            id="enableAveraging"
            v-model="chartData.averageData.enabled"
            class="mr-2"
          />
          <label for="enableAveraging" class="text-sm font-medium text-gray-700">
            Enable dimension averaging
          </label>
        </div>

        <!-- Averaging Configuration -->
        <div v-if="chartData.averageData && chartData.averageData.enabled" class="space-y-4">
          <!-- Selected Dimensions for Averaging -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Dimensions to Average
            </label>
            <div class="space-y-2">
              <div
                v-for="dimension in chartData.dimensions"
                :key="dimension"
                class="flex items-center justify-between p-2 border rounded"
              >
                <div class="flex items-center">
                  <input
                    type="checkbox"
                    :id="`avg-${dimension}`"
                    v-model="chartData.averageData.dimensions"
                    :value="dimension"
                    class="mr-2"
                  />
                  <label :for="`avg-${dimension}`" class="text-sm">
                    {{ getDimensionLabel(dimension) }}
                  </label>
                </div>
                <div class="flex items-center">
                  <label class="text-xs text-gray-500 mr-2">Weight:</label>
                  <input
                    type="number"
                    v-model="chartData.averageData.weights[dimension]"
                    min="0"
                    max="100"
                    step="1"
                    class="w-16 px-2 py-1 text-sm border rounded"
                    placeholder="50"
                  />
                  <span class="text-xs text-gray-500 ml-1">%</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Average Configuration -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Average Title (English)
              </label>
              <InputText v-model="chartData.averageData.title.en" class="w-full"
               placeholder="e.g., Knowledge Sharing Average" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Average Title (German)
              </label>
              <InputText v-model="chartData.averageData.title.de" class="w-full"
               placeholder="e.g., Wissensaustausch Durchschnitt" />
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Average Description (English)
              </label>
              <InputText v-model="chartData.averageData.description.en" class="w-full"
               placeholder="e.g., Combined knowledge sharing and sourcing score" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Average Description (German)
              </label>
              <InputText v-model="chartData.averageData.description.de" class="w-full"
               placeholder="e.g., Kombinierter Wissensaustausch und Beschaffungsscore" />
            </div>
          </div>

          <!-- Average Color Configuration -->
          <div class="flex gap-4">
            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Average Background Color
              </label>
              <ColorPicker
                v-model="chartData.averageData.backgroundColor"
                :defaultColor="'rgba(255, 107, 107, 0.5)'"
                v-if="chartData.averageData"
              />
            </div>
            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Average Border Color
              </label>
              <ColorPicker
                v-model="chartData.averageData.borderColor"
                :defaultColor="'rgb(255, 107, 107)'"
                v-if="chartData.averageData"
              />
            </div>
          </div>

          <!-- Auto-generate Average Button -->
          <div class="flex justify-end">
            <Button
              label="Generate Average Dataset"
              icon="pi pi-calculator"
              class="p-button-text"
              @click="generateAverageDataset"
              :disabled="!chartData.averageData.dimensions
               || chartData.averageData.dimensions.length < 2"
            />
          </div>
        </div>
      </div>

      <!-- Special Dimensions Configuration -->
      <div v-if="availableDimensions && availableDimensions.length > 0"
        class="field p-4 border rounded bg-green-50">
        <h4 class="text-lg font-medium text-gray-700 mb-3">Special Dimensions Configuration</h4>
        <p class="text-sm text-gray-600 mb-4">
          Configure dimensions that reference other collections (e.g., user IDs)
        </p>

        <div class="space-y-4">
          <div
            v-for="dimension in availableDimensions"
            :key="dimension._id"
            class="p-3 border rounded bg-white"
          >
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center">
                <input
                  type="checkbox"
                  :id="`special-${dimension._id}`"
                  v-model="chartData.specialDimensionsData[dimension._id].enabled"
                  class="mr-2"
                />
                <label :for="`special-${dimension._id}`" class="text-sm font-medium">
                  {{ getDimensionDisplayName(dimension) }}
                </label>
              </div>
            </div>

            <div v-if="chartData.specialDimensionsData[dimension._id].enabled" class="space-y-3">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Collection
                  </label>
                  <select
                    v-model="chartData.specialDimensionsData[dimension._id].collection"
                    class="w-full px-3 py-2 border rounded text-sm"
                  >
                    <option value="users">Users</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Display Field
                  </label>
                  <select
                    v-model="chartData.specialDimensionsData[dimension._id].displayField"
                    class="w-full px-3 py-2 border rounded text-sm"
                  >
                    <option value="name">Name</option>
                  </select>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  API Fetch Path
                </label>
                <InputText
                  v-model="chartData.specialDimensionsData[dimension._id].fetchPath"
                  placeholder="/api/users/{id}"
                  class="w-full"
                />
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Value When ID Exists
                  </label>
                  <InputNumber
                    v-model="chartData.specialDimensionsData[dimension._id].valueMapping.hasValue"
                    :min="0"
                    :max="100"
                    class="w-full"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Value When No ID
                  </label>
                  <InputNumber
                    v-model="chartData.specialDimensionsData[dimension._id].valueMapping.noValue"
                    :min="0"
                    :max="100"
                    class="w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Chart Data -->
      <div class="field">
        <label class="block text-sm font-medium text-gray-700 mb-1">Chart Data</label>
        <div class="space-y-4">
          <!-- Label Generation -->
          <div class="field">
            <label class="block text-sm font-medium text-gray-700 mb-1">Label Generation</label>
            <div class="flex gap-4 items-end">
              <div class="flex-1">
                <Dropdown
                  v-model="labelGenerationType"
                  :options="labelGenerationOptions"
                  optionLabel="name"
                  optionValue="value"
                  class="w-full"
                  placeholder="Select label generation type"
                />
              </div>
              <Button
                label="Generate Labels"
                icon="pi pi-sync"
                class="p-button-text"
                @click="generateLabels"
                :disabled="!labelGenerationType"
              />
            </div>
          </div>

          <!-- Time Span Settings (shown only when time span is selected) -->
          <div v-if="labelGenerationType === 'timeSpan'" class="field p-4 border rounded">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Start Year</label>
                <InputNumber v-model="timeSpan.startYear" :min="1900" :max="2100" class="w-full" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">End Year</label>
                <InputNumber v-model="timeSpan.endYear" :min="1900" :max="2100" class="w-full" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Period (years)</label>
                <InputNumber v-model="timeSpan.period" :min="1" :max="10" class="w-full" />
              </div>
            </div>
          </div>

          <!-- Labels -->
          <div class="field">
            <label class="block text-sm font-medium text-gray-700 mb-1">Labels</label>
            <Chips v-model="chartData.data.labels" class="w-full" />
          </div>

          <!-- Datasets -->
          <div class="field">
            <label class="block text-sm font-medium text-gray-700 mb-1">Datasets</label>
            <div
              v-for="(dataset, idx) in chartData.data.datasets"
              :key="idx"
              class="mb-4 p-4 border rounded"
            >
              <div class="flex justify-between items-center mb-2">
                <h5>Dataset {{ idx + 1 }}</h5>
                <Button
                  icon="pi pi-trash"
                  class="p-button-rounded p-button-text p-button-danger"
                  @click="removeDataset(idx)"
                />
              </div>
              <div class="space-y-2">
                <InputText v-model="dataset.label" placeholder="Dataset Label" class="w-full" />
                <Chips v-model="dataset.data" placeholder="Enter numeric values" class="w-full" />
                <div class="flex gap-4">
                  <div class="flex-1">
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Background Color
                    </label>
                    <ColorPicker
                      v-model="dataset.backgroundColor"
                      :defaultColor="'rgba(255, 99, 132, 0.5)'"
                    />
                  </div>
                  <div class="flex-1">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Border Color</label>
                    <ColorPicker
                      v-model="dataset.borderColor"
                      :defaultColor="'rgb(255, 99, 132)'"
                    />
                  </div>
                </div>
              </div>
            </div>
            <Button
              label="Add Dataset"
              icon="pi pi-plus"
              class="p-button-text"
              @click="addDataset"
            />
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="onCancel" />
      <Button label="Save" icon="pi pi-check" @click="onSave" />
    </template>
  </Dialog>
</template>

<script>
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import MultiSelect from 'primevue/multiselect';
import Chips from 'primevue/chips';
import ColorPicker from 'primevue/colorpicker';
import { mapState } from 'vuex';

export default {
  name: 'ChartDialog',
  components: {
    Button,
    Dialog,
    InputText,
    InputNumber,
    Dropdown,
    MultiSelect,
    Chips,
    ColorPicker,
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    chart: {
      type: Object,
      default: () => ({
        type: 'bar',
        title: {
          en: '',
          de: '',
        },
        description: {
          en: '',
          de: '',
        },
        data: {
          labels: [],
          datasets: [
            {
              label: 'Dataset 1',
              data: [],
              backgroundColor: 'rgba(255, 99, 132, 0.5)',
              borderColor: 'rgb(255, 99, 132)',
            },
          ],
        },
        dimensions: [],
        options: {},
      }),
    },
    assessmentId: {
      type: String,
      required: true,
    },
  },
  data() {
    const defaultBarBoxplot = () => ({
      enabled: false,
      ownResults: false,
      teamResults: true,
    });
    return {
      chartData: {
        ...this.chart,
        visibility: this.chart?.visibility ?? 'both',
        barBoxplotWhenAggregated: this.chart?.barBoxplotWhenAggregated
          ? { ...defaultBarBoxplot(), ...this.chart.barBoxplotWhenAggregated }
          : defaultBarBoxplot(),
        // Add averaging data as separate object
        averageData: this.chart?.averageData || {
          dimensions: [],
          weights: {},
          title: {
            en: '',
            de: '',
          },
          description: {
            en: '',
            de: '',
          },
          backgroundColor: 'rgba(255, 107, 107, 0.5)',
          borderColor: 'rgb(255, 107, 107)',
          enabled: false,
        },
        // Add special dimensions data as separate object
        specialDimensionsData: this.chart?.specialDimensionsData || {},
      },
      labelGenerationType: null,
      timeSpan: {
        startYear: new Date().getFullYear() - 10,
        endYear: new Date().getFullYear(),
        period: 1,
      },
    };
  },
  computed: {
    ...mapState('assessmentSchemas', ['currentAssessmentSchema']),
    ...mapState('dimensions', ['dimensions']),
    ...mapState('config', { configData: 'data' }),
    minDatasetsForAggDisplay() {
      const n = this.configData?.minOfDatasetsForAggResults;
      return typeof n === 'number' && !Number.isNaN(n) ? n : 5;
    },
    appLanguage() {
      return this.$i18n.locale;
    },
    dialogVisible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    chartTypes() {
      return [
        { name: 'Bar', value: 'bar' },
        { name: 'Line', value: 'line' },
        { name: 'Box plot', value: 'boxplot' },
        { name: 'Pie', value: 'pie' },
        { name: 'Doughnut', value: 'doughnut' },
      ];
    },
    visibilityOptions() {
      return [
        { name: this.$t('ChartDialog.visibilityOwn'), value: 'own' },
        { name: this.$t('ChartDialog.visibilityTeam'), value: 'team' },
        { name: this.$t('ChartDialog.visibilityBoth'), value: 'both' },
      ];
    },
    barBoxplotVisibilityHint() {
      const v = this.chartData.visibility || 'both';
      const bb = this.chartData.barBoxplotWhenAggregated;
      if (this.chartData.type !== 'bar' || !bb?.enabled) return '';
      if (v === 'own' && bb.teamResults && !bb.ownResults) {
        return this.$t('ChartDialog.visibilityBarBoxplotTeamOnlyNoOwn');
      }
      if (v === 'team' && bb.ownResults && !bb.teamResults) {
        return this.$t('ChartDialog.visibilityBarBoxplotOwnOnlyNoTeam');
      }
      return '';
    },
    labelGenerationOptions() {
      return [
        { name: 'Use Selected Dimensions', value: 'dimensions' },
        { name: 'Time Span', value: 'timeSpan' },
        { name: 'Manual Entry', value: 'manual' },
      ];
    },
    availableDimensions() {
      // Filter dimensions to only show those assigned to the current assessment schema
      const schemaDimensions = this.currentAssessmentSchema?.dimensions;
      if (!schemaDimensions || !Array.isArray(schemaDimensions)) {
        return [];
      }

      // Get the dimension IDs from the current assessment schema
      const schemaDimensionIds = schemaDimensions.map((dim) => (typeof dim === 'string' ? dim : dim._id || dim.id));

      // Filter all dimensions to only include those assigned to this schema
      return this.dimensions.filter((dim) => schemaDimensionIds.includes(dim._id));
    },
    isValid() {
      const {
        title,
        description,
        data,
        dimensions,
        type,
      } = this.chartData;

      // Check required fields based on ChartDto
      if (!type || !['bar', 'line', 'boxplot', 'pie', 'doughnut'].includes(type)) return false;
      if (!title?.en || !title?.de) return false;
      if (!description?.en || !description?.de) return false;
      if (!Array.isArray(dimensions) || dimensions.length === 0) return false;

      // Check data structure based on ChartDataDto
      if (!data?.labels || !Array.isArray(data.labels) || data.labels.length === 0) return false;
      if (!data?.datasets || !Array.isArray(data.datasets) || data.datasets.length === 0) {
        return false;
      }

      // Boxplot: only requires labels and at least one dataset with data array
      if (type === 'boxplot') {
        return data.datasets.every((dataset) => {
          if (!dataset.label || typeof dataset.label !== 'string') return false;
          if (!Array.isArray(dataset.data)) return false;
          return true;
        });
      }

      // Check each dataset based on ChartDatasetDto (bar, line, pie, doughnut)
      return data.datasets.every((dataset) => {
        if (!dataset.label || typeof dataset.label !== 'string') return false;
        if (!Array.isArray(dataset.data) || dataset.data.length === 0) return false;
        if (!dataset.data.every((value) => typeof value === 'number')) return false;
        return true;
      });
    },
  },
  watch: {
    chart: {
      handler(newChart, oldChart) {
        // Only update if the chart prop actually changed from outside
        if (newChart !== oldChart && newChart) {
          const defaultBarBoxplot = () => ({
            enabled: false,
            ownResults: false,
            teamResults: true,
          });
          this.chartData = {
            ...newChart,
            visibility: newChart.visibility ?? 'both',
            barBoxplotWhenAggregated: newChart.barBoxplotWhenAggregated
              ? { ...defaultBarBoxplot(), ...newChart.barBoxplotWhenAggregated }
              : defaultBarBoxplot(),
            // Preserve averaging data as separate object
            averageData: this.chartData?.averageData || {
              dimensions: [],
              weights: {},
              title: { en: '', de: '' },
              description: { en: '', de: '' },
              backgroundColor: 'rgba(255, 107, 107, 0.5)',
              borderColor: 'rgb(255, 107, 107)',
              enabled: false,
            },
            // Preserve special dimensions data as separate object
            specialDimensionsData: this.chartData?.specialDimensionsData || {},
          };
        }
      },
      immediate: true,
    },
    'chartData.dimensions': {
      handler() {
        this.onDimensionsChange();
      },
      deep: true,
    },
    availableDimensions: {
      handler() {
        this.initializeSpecialDimensionsData();
      },
      deep: true,
    },
    modelValue(open) {
      if (open) {
        this.$store.dispatch('config/fetchConfig').catch(() => {});
      }
    },
  },
  methods: {
    onBarBoxplotEnabledChange(event) {
      if (event?.target?.checked && this.chartData.barBoxplotWhenAggregated) {
        this.chartData.barBoxplotWhenAggregated.ownResults = false;
        this.chartData.barBoxplotWhenAggregated.teamResults = true;
      }
    },
    addDataset() {
      this.chartData.data.datasets.push({
        label: `Dataset ${this.chartData.data.datasets.length + 1}`,
        data: [],
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        borderColor: 'rgb(255, 99, 132)',
      });
    },
    removeDataset(index) {
      this.chartData.data.datasets.splice(index, 1);
    },
    generateLabels() {
      if (this.labelGenerationType === 'dimensions') {
        // Use selected dimensions as labels
        if (!this.chartData.dimensions?.length) {
          console.error('No dimensions selected');
          return;
        }

        // Use the selected dimension values directly as labels
        this.chartData.data.labels = this.chartData.dimensions;

        // Also initialize the dataset data array with zeros for each dimension
        if (this.chartData.data.datasets && this.chartData.data.datasets.length > 0) {
          this.chartData.data.datasets[0].data = new Array(this.chartData.data.labels.length).fill(
            0,
          );
        }
      } else if (this.labelGenerationType === 'timeSpan') {
        // Generate time span labels
        const labels = [];
        for (
          let year = this.timeSpan.startYear;
          year <= this.timeSpan.endYear;
          year += this.timeSpan.period
        ) {
          labels.push(year.toString());
        }
        this.chartData.data.labels = labels;
      }
    },

    getDimensionLabel(dimensionValue) {
      const dimension = this.dimensions.find((d) => d.value === dimensionValue);
      const locale = this.$i18n?.locale || 'en';
      return dimension?.translation?.[locale] || dimensionValue;
    },

    getDimensionDisplayName(dimension) {
      const locale = this.$i18n?.locale || 'en';
      // Try to get the translated name first
      if (dimension.translation?.[locale]) {
        return dimension.translation[locale];
      }
      // Fallback to the dimension value/name
      return dimension.value || dimension.name || dimension._id;
    },

    generateAverageDataset() {
      if (!this.chartData.averageData.dimensions
       || this.chartData.averageData.dimensions.length < 2) {
        console.error('Need at least 2 dimensions to average');
        return;
      }

      // Calculate total weight
      const totalWeight = this.chartData.averageData.dimensions
        .reduce((sum, dim) => sum + (this.chartData.averageData.weights[dim] || 50), 0);

      // Normalize weights
      const normalizedWeights = {};
      this.chartData.averageData.dimensions.forEach((dim) => {
        normalizedWeights[dim] = (this.chartData.averageData.weights[dim] || 50) / totalWeight;
      });

      // Create average dataset
      const averageDataset = {
        label: this.chartData.averageData.title[this.appLanguage] || this.chartData.averageData.title.en || 'Average',
        data: new Array(this.chartData.data.labels.length).fill(0),
        backgroundColor: this.chartData.averageData.backgroundColor,
        borderColor: this.chartData.averageData.borderColor,
        borderWidth: 2,
        fill: false,
        tension: 0.4,
        // Store averaging metadata
        averagingMetadata: {
          dimensions: this.chartData.averageData.dimensions,
          weights: normalizedWeights,
          title: this.chartData.averageData.title,
          description: this.chartData.averageData.description,
        },
      };

      // Add to datasets
      this.chartData.data.datasets.push(averageDataset);

      console.log('Generated average dataset:', averageDataset);
    },

    // Watch for dimension changes to reset averaging
    onDimensionsChange() {
      // Reset averaging when dimensions change
      this.chartData.averageData.enabled = false;
      this.chartData.averageData.dimensions = [];
      this.chartData.averageData.weights = {};

      // Initialize weights for new dimensions
      if (this.chartData.dimensions) {
        this.chartData.dimensions.forEach((dim) => {
          this.chartData.averageData.weights[dim] = 50; // Default 50% weight
        });
      }
    },

    // Initialize special dimensions data for all available dimensions
    initializeSpecialDimensionsData() {
      if (this.availableDimensions) {
        this.availableDimensions.forEach((dim) => {
          if (!this.chartData.specialDimensionsData[dim._id]) {
            this.chartData.specialDimensionsData[dim._id] = {
              enabled: false,
              collection: 'users',
              displayField: 'name',
              fetchPath: '/api/users/{id}',
              valueMapping: {
                hasValue: 1,
                noValue: 0,
              },
            };
          }
        });
      }
    },

    onSave() {
      // Ensure colors are in the correct format
      this.chartData.data.datasets = this.chartData.data.datasets.map((dataset) => ({
        ...dataset,
        backgroundColor: dataset.backgroundColor || 'rgba(255, 99, 132, 0.5)',
        borderColor: dataset.borderColor || 'rgb(255, 99, 132)',
      }));
      this.$emit('save', this.chartData);
    },
    onCancel() {
      this.$emit('cancel');
    },
  },
  mounted() {
    // Ensure dimensions are fetched when the dialog is mounted
    this.$store.dispatch('dimensions/fetchDimensions');
    this.$store.dispatch('config/fetchConfig').catch(() => {});
  },
};
</script>
